/**
 * The create flow's selection model (ui#94, epic ui#93): each picked/dropped file plus the
 * folder-relative path it came with. A wrapper type is REQUIRED — `File.webkitRelativePath` is
 * read-only and empty for drag-and-drop captures, so the traversal-built path must travel beside
 * the `File`. Seeding (P2P) and hashing still consume the raw `File`s; only registration payloads
 * and the file lists care about the path.
 */

/** One user-selected file plus where it sat inside the picked/dropped folder (absent = flat). */
export interface SelectedFile {
	file: File;
	/** Forward-slash path INCLUDING the leaf name (`docs/report.pdf`); absent for plain files. */
	relativePath?: string;
}

/** What capture surfaces hand around: a raw `File` (plain pick/drop) or a path-aware entry. */
export type SelectedFileInput = File | SelectedFile;

/** Normalizes either input shape to a {@link SelectedFile} (a raw `File` becomes a flat entry). */
export function toSelectedFile(input: SelectedFileInput): SelectedFile {
	return "file" in input ? input : {file: input};
}

/**
 * Wraps one picker-chosen file, reading `webkitRelativePath` — non-empty ONLY for
 * `webkitdirectory` (folder picker) selections, so plain file picks stay flat.
 */
export function fromPickedFile(file: File): SelectedFile {
	const relativePath = file.webkitRelativePath;
	return relativePath ? {file, relativePath} : {file};
}

/**
 * Everything one `<input type="file">` change yielded, each entry wrapped path-aware.
 *
 * EVERY picker must go through this rather than `Array.from(input.files)`: a raw `File` list is
 * structurally assignable wherever a selection is expected, so handing one straight on compiles
 * cleanly and silently throws the folder structure away — which is exactly what the add-files picker
 * did until ui#1308. Surfaces that consume a selection therefore type their input as
 * {@link SelectedFile}`[]`, not {@link SelectedFileInput}`[]`, so skipping this wrap is a tsc error
 * instead of a data loss nobody sees.
 */
export function fromPickedFiles(files: FileList | null | undefined): SelectedFile[] {
	return Array.from(files ?? []).map(fromPickedFile);
}
